import { toast } from 'react-toastify'

import { generateAumhumLocalMailIdForUser } from 'src/clinic/components/ClinicManageBookingsPage/ClinicManageBookingsHelper'
import { buildPatientUpsertPayload } from 'src/clinic/components/ClinicManageBookingsPage/patientForm/utils/patientFormDataUtils'
import { getCPDataWithoutParams, postCPDataWithoutParams } from 'src/clinic/services'
import { BLANK_PATIENT_DATA } from 'src/consumer/constants'
import { getAgeFromDobWithTime, getDOBWithTime } from 'src/consumer/helpers/profilePageHelper'
import { ensurePlusPrefix } from 'src/consumer/helpers/utilFunctions'
// import {
//   ensurePlusPrefix,
//   getDataMapFromCommonResponse,
//   isCommonResponseSuccessful,
//   generateAumhumLocalMailIdForUser,
// } from 'src/consumer/helpers/utilityFunctions'
import { addOrUpdatePatientsInIdb } from 'src/emr/database/indexedDbDao'
import { getDataMapFromCommonResponse, isCommonResponseSuccessful } from 'src/mentor/helpers/utilityFunctions'

export function cleanAttendantsArray(attendantsArray) {
  if (Array.isArray(attendantsArray)) {
    return attendantsArray.filter((item) => item?.name)
  }
  return []
}

// Server returns this code when a Profile with the same userName (mobile/email)
// already exists in a non-active state. The payload carries existingUserId so the
// support portal can offer a one-click Re-enable + retry. Source of truth:
// in.aumhum.aumhum.definitions.RespCodes.EXISTING_USER_DISABLED
export const EXISTING_USER_DISABLED_CODE = 7057

const SUPPORT_REACTIVATE_USER_API = '/api/secure/cc/support/patient/reactivate-user'

function describeUserInactiveState(payload) {
  const labels = []
  if (payload?.enabled === false) labels.push('disabled')
  if (payload?.locked) labels.push('locked')
  if (payload?.expired) labels.push('expired')
  return labels.length > 0 ? labels.join(' / ') : 'inactive'
}

async function tryReactivateExistingUserAndRetry({
  response,
  createPatientApiSignature,
  retryCreatePatient,
}) {
  if (response?.data?.code !== EXISTING_USER_DISABLED_CODE) return null
  // Reactivate endpoint is gated to support roles. Don't offer the flow when the
  // caller is using a non-support API signature (mentor portal etc).
  if (!createPatientApiSignature || !createPatientApiSignature.includes('/cc/support/')) {
    toast.error(response?.data?.message || 'A user with this mobile/email already exists.')
    return null
  }
  const payload = response?.data?.data?.payload
  const existingUserId = payload?.existingUserId
  if (!existingUserId) {
    toast.error(response?.data?.message || 'A user with this mobile/email already exists.')
    return null
  }
  const stateText = describeUserInactiveState(payload)
  const userName = payload?.userName || 'this mobile/email'
  const confirmed =
    typeof window !== 'undefined' &&
    window.confirm(
      `A user with ${userName} already exists but is ${stateText}.\n\n` +
        'Re-enable that user and continue creating this patient?',
    )
  if (!confirmed) {
    return null
  }
  const reactivateResp = await postCPDataWithoutParams(SUPPORT_REACTIVATE_USER_API, {
    userId: existingUserId,
  })
  if (reactivateResp.status !== 200 || !isCommonResponseSuccessful(reactivateResp?.data?.code)) {
    toast.error(reactivateResp?.data?.message || 'Could not re-enable the existing user.')
    return null
  }
  toast.success('Existing user re-enabled. Retrying patient creation...')
  return await retryCreatePatient()
}

export async function createNewPatient(
  clinicId,
  clinicBrandId,
  mentorProfileData,
  newPatientInputData,
  setNewPatientSubmissionLoading,
  forMentor,
  setSelectedPatient,
  setNewPatientInputData,
  setIsNewPatient,
  setRelatedToPatient,
  setRelatedPatientLoading,
  makeAnonymousPatient,
  createPatientApiSignature = '/api/secure/patient/new',
  patientSearchApiSignature = '/api/secure/mentor/patient/list',
  setPatientSaveMessage,
  canTakeImportId = false,
  defaultPatientImportedFrom,
) {
  console.log(newPatientInputData)
  let dataToSubmit = buildPatientUpsertPayload(newPatientInputData, {
    clinicId,
    clinicBrandId,
    mentorId: mentorProfileData?.id,
    anonymous: makeAnonymousPatient,
  })
  const addAddToRelatedPatientStateAfterCreation = newPatientInputData?.relatedToExistingPatient?.id || null
  if (
    Boolean(makeAnonymousPatient) &&
    !Boolean(dataToSubmit?.mobile) &&
    !Boolean(dataToSubmit?.email)
  ) {
    dataToSubmit.email = generateAumhumLocalMailIdForUser(dataToSubmit?.name)
  }
  if (canTakeImportId) {
    dataToSubmit.importData = {
      ...newPatientInputData?.importData,
      importedFrom: defaultPatientImportedFrom,
    }
  }

  setNewPatientSubmissionLoading(true)
  // distinction between clinic assistant api and mentor api
  const submitCreatePatient = () => postCPDataWithoutParams(createPatientApiSignature, dataToSubmit)
  let response
  if (forMentor) {
    response = await submitCreatePatient()
  } else {
    response = await submitCreatePatient()
  }
  if (
    response.status === 200 &&
    !isCommonResponseSuccessful(response?.data?.code) &&
    response?.data?.code === EXISTING_USER_DISABLED_CODE
  ) {
    const retried = await tryReactivateExistingUserAndRetry({
      response,
      createPatientApiSignature,
      retryCreatePatient: submitCreatePatient,
    })
    if (retried) {
      response = retried
    }
  }
  if (response.status === 200 && isCommonResponseSuccessful(response?.data?.code)) {
    const returnedPatient = getDataMapFromCommonResponse(response)?.patient
    if (forMentor) {
      addOrUpdatePatientsInIdb([returnedPatient])
    }
    const toSetSelectedPatientData = {
      ...(response?.data?.data?.patient || {}),
      age: getAgeFromDobWithTime(response?.data?.data?.patient?.dateOfBirth),
    }
    setSelectedPatient(toSetSelectedPatientData)
    setNewPatientInputData(BLANK_PATIENT_DATA)
    setIsNewPatient(false)
    if (addAddToRelatedPatientStateAfterCreation) {
      setRelatedToPatient(null)
      setRelatedPatientLoading(true)
      const responseTwo = await getCPDataWithoutParams(
        `${patientSearchApiSignature}?patientId=${addAddToRelatedPatientStateAfterCreation}&clinicId=${clinicId}`,
      )
      setRelatedPatientLoading(false)
      if (responseTwo.status === 200 && responseTwo?.data && responseTwo?.data?.[0]) {
        setRelatedToPatient(responseTwo?.data?.[0])
      } else {
        toast.error('Error finding related patient data. Contact support.')
      }
    }
    return toSetSelectedPatientData
  } else {
    console.log({ response })
    if (response?.data?.code === 1011) {
      setPatientSaveMessage({
        type: 'error',
        message: 'Please create this patient as a relative of another patient.',
      })
    } else {
      setPatientSaveMessage({ type: 'error', message: 'Error creating new patient' })
    }
    toast.error('Error creating new patient', {
      autoClose: 2000,
    })
  }
  setNewPatientSubmissionLoading(false)
}
